import type React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { BottomSheetShell } from './BottomSheetShell';

interface InfoBottomSheetProps {
  open: boolean;
  onClose: () => void;
}

/** The habits that keep a match readable for the pipeline. Two of them are the
    same conditions that define a block (same partner, same side); the third is
    what keeps the players tellable apart between frames. */
const PRACTICE_KEYS = ['samePartner', 'sameSide', 'outfits'] as const;

/**
 * What a player can do ON COURT to get a better report — as opposed to
 * {@link InfoBottomSheet}, which explains how to read the one they already
 * have. Same shell, so both read as the same kind of aside.
 */
export const BestPracticesBottomSheet: React.FC<InfoBottomSheetProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('report');

  return (
    <BottomSheetShell open={open} onClose={onClose}>
      <h3 className="mb-1 text-base font-semibold text-slate-800">
        {t('bestPractices.title')}
      </h3>
      <p className="mb-4 text-sm text-slate-500">{t('bestPractices.intro')}</p>
      <ul className="space-y-3">
        {PRACTICE_KEYS.map((k) => (
          <li key={k} className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-50 text-sky-600">
              <Check className="h-3.5 w-3.5" strokeWidth={2.6} />
            </span>
            <span className="text-sm text-slate-600">
              {t(`bestPractices.${k}` as never) as string}
            </span>
          </li>
        ))}
      </ul>
    </BottomSheetShell>
  );
};

export const InfoBottomSheet: React.FC<InfoBottomSheetProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation('report');

  return (
    <BottomSheetShell open={open} onClose={onClose}>
      <h3 className="mb-3 text-base font-semibold text-slate-800">
        {t('info.title')}
      </h3>
      <div className="space-y-3 text-sm text-slate-600">
        <p>{t('info.description1')}</p>
        <p>{t('info.description2')}</p>
        <p>{t('info.consistentMeaning')}</p>
        <ul className="list-disc pl-5 text-slate-600">
          <li>
            <span className="font-semibold text-slate-700">
              {t('info.sameTeammates')}
            </span>
          </li>
          <li>
            <span className="font-semibold text-slate-700">
              {t('info.samePosition')}
            </span>{' '}
            {t('info.leftOrRight')}
          </li>
        </ul>
        <p>{t('info.swipeNav')}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{t('info.navigation')}</span>
          <div className="flex items-center gap-1.5">
            <ChevronLeft className="h-4 w-4 text-slate-400" />
            <div className="flex gap-1">
              <div className="h-1.5 w-4 rounded-full bg-sky-500" />
              <div className="h-1.5 w-1.5 rounded-full bg-slate-300" />
              <div className="h-1.5 w-1.5 rounded-full bg-slate-300" />
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </div>
        </div>
      </div>
    </BottomSheetShell>
  );
};
